import type React from "react"
import { useState, useRef, useEffect } from "react"
import {
  Box,
  Container,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  AppBar,
  Toolbar,
  Chip,
  Divider,
} from "@mui/material"
import SendIcon from "@mui/icons-material/Send"
import SmartToyIcon from "@mui/icons-material/SmartToy"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import HomeIcon from "@mui/icons-material/Home"
import { useNavigate } from "react-router-dom"
import { GEMINI_API_KEY } from "../../utils/ApiLinks"

interface Message {
  id: string
  content: string
  role: "user" | "assistant"
  timestamp: Date
}

const ChatbotPage: React.FC = () => {
  const navigate = useNavigate()
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content:
        "¡Hola! Soy el asistente virtual de Alien Realty. Puedo ayudarte a encontrar propiedades, responder preguntas sobre el mercado inmobiliario en Costa Rica, o asistirte con el proceso de compra o alquiler. ¿En qué puedo ayudarte hoy?",
      role: "assistant",
      timestamp: new Date(),
    },
  ])
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSendMessage = async () => {
    if (!input.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      role: "user",
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInput("")
    setIsLoading(true)

    try {
      // Llamada a la API de Gemini
      const response = await fetch(
        "https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-goog-api-key": GEMINI_API_KEY,
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: `Eres un asistente virtual especializado en bienes raíces para una agencia llamada Alien Realty en Costa Rica.
                  
                  Proporciona información útil, precisa y amigable sobre:
                  - Propiedades disponibles en Costa Rica
                  - Procesos de compra, venta o alquiler de propiedades
                  - Información sobre diferentes regiones y provincias de Costa Rica
                  - Consejos para inversión inmobiliaria
                  - Trámites legales relacionados con bienes raíces
                  
                  Mantén tus respuestas concisas, informativas y orientadas al cliente.
                  
                  Consulta del usuario: ${input}`,
                  },
                ],
              },
            ],
          }),
        },
      )

      const data = await response.json()

      if (data.candidates && data.candidates[0]?.content?.parts[0]?.text) {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          content: data.candidates[0].content.parts[0].text,
          role: "assistant",
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, botResponse])
      } else {
        throw new Error("No se pudo obtener una respuesta válida")
      }
    } catch (error) {
      console.error("Error al comunicarse con Gemini:", error)

      // Fallback a respuestas simuladas en caso de error
      const botResponses = [
        "Claro, puedo ayudarte a encontrar propiedades en esa zona. ¿Buscas para comprar o alquilar?",
        "Las propiedades en esa área tienen un precio promedio de $150,000 a $300,000 dependiendo del tamaño y las amenidades.",
        "¡Por supuesto! Tenemos varias opciones que podrían interesarte. ¿Prefieres ver primero las más recientes?",
        "Para iniciar el proceso de compra, necesitarás preparar algunos documentos. ¿Te gustaría que te explique los pasos?",
        "Esa zona es muy popular por su cercanía a escuelas, centros comerciales y tiene excelente seguridad.",
      ]

      const randomResponse = botResponses[Math.floor(Math.random() * botResponses.length)]

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: randomResponse,
        role: "assistant",
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleQuickQuestion = (question: string) => {
    setInput(question)
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <AppBar position="static" sx={{ backgroundColor: "#4CAF50" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate("/")} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SmartToyIcon />
            <Typography variant="h6">Asistente Virtual de Alien Realty</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton color="inherit" onClick={() => navigate("/")}>
            <HomeIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ flexGrow: 1, display: "flex", flexDirection: "column", py: 2 }}>
        <Paper
          elevation={3}
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              p: 3,
              flexGrow: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              backgroundColor: "#f5f5f5",
            }}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: "flex",
                  justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: message.role === "user" ? "row-reverse" : "row",
                    alignItems: "flex-start",
                    gap: 1,
                    maxWidth: "80%",
                  }}
                >
                  {message.role === "assistant" && (
                    <Avatar sx={{ bgcolor: "#4CAF50", width: 36, height: 36 }}>
                      <SmartToyIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                  )}
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: message.role === "user" ? "#E3F2FD" : "white",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body1">{message.content}</Typography>
                    <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                      {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            ))}
            {isLoading && (
              <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar sx={{ bgcolor: "#4CAF50", width: 36, height: 36 }}>
                    <SmartToyIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <CircularProgress size={24} sx={{ color: "#4CAF50" }} />
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          <Box sx={{ p: 2, backgroundColor: "#f9f9f9" }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
              Preguntas frecuentes:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              <Chip
                label="Propiedades en San José"
                onClick={() => handleQuickQuestion("¿Qué propiedades tienen disponibles en San José?")}
                sx={{ cursor: "pointer", backgroundColor: "#E8F5E9", color: "#2E7D32" }}
              />
              <Chip
                label="Proceso de compra"
                onClick={() => handleQuickQuestion("¿Cuáles son los pasos para comprar una propiedad?")}
                sx={{ cursor: "pointer", backgroundColor: "#E8F5E9", color: "#2E7D32" }}
              />
              <Chip
                label="Zonas turísticas"
                onClick={() => handleQuickQuestion("¿Qué zonas turísticas son buenas para invertir?")}
                sx={{ cursor: "pointer", backgroundColor: "#E8F5E9", color: "#2E7D32" }}
              />
              <Chip
                label="Financiamiento"
                onClick={() => handleQuickQuestion("¿Qué opciones de financiamiento existen?")}
                sx={{ cursor: "pointer", backgroundColor: "#E8F5E9", color: "#2E7D32" }}
              />
              <Chip
                label="Requisitos legales"
                onClick={() => handleQuickQuestion("¿Cuáles son los requisitos legales para extranjeros?")}
                sx={{ cursor: "pointer", backgroundColor: "#E8F5E9", color: "#2E7D32" }}
              />
            </Box>
          </Box>

          <Box
            component="form"
            sx={{
              p: 2,
              backgroundColor: "white",
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              gap: 1,
            }}
            onSubmit={(e) => {
              e.preventDefault()
              handleSendMessage()
            }}
          >
            <TextField
              fullWidth
              placeholder="Escribe tu mensaje..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              variant="outlined"
              disabled={isLoading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                },
              }}
            />
            <IconButton
              color="primary"
              type="submit"
              disabled={!input.trim() || isLoading}
              sx={{
                bgcolor: "#4CAF50",
                color: "white",
                "&:hover": { bgcolor: "#3e8e41" },
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default ChatbotPage
